// XPEN-3590 / XPEN-3591: turn the server's duplicate refusals into clear, translated messages.
//
// Upload (XPEN-3590): PUT /expenses_account/add/<id> refuses a receipt XPENS already has with 422 and
// {"message": "duplicate"}. Since XPEN-3589 the body can also name where the copy is:
//   expenses_account: {id, identifiant} | null, received_at: epoch ms | null.
// Both are null (or absent on an older server) when the server names no report -- which
// includes a caller who may not open the owner's other report (XPEN-3589 "Option A").
//
// Approval (XPEN-3591): approving a line, resending a request, requesting line approval and
// moving a denied receipt are refused with 409 {"error": "expense_duplicate_in_other_report",
// "expenses_account": {id, identifiant}} when the receipt is already in another expense
// account (XPEN-3556). A bulk approval row carries the same code as "reason".

const DUPLICATE = "duplicate";
const DUPLICATE_IN_OTHER_REPORT = "expense_duplicate_in_other_report";
const KEYS = "expense_duplicate";
// An expense account id as the server sends it (a MongoDB ObjectId). Only such an id is
// turned into a link, so an unexpected value can never point elsewhere in the app.
const ACCOUNT_ID = /^[0-9a-f]{24}$/i;

// Only reached with a non-empty report id (see below), so "undefined" never equals "undefined".
const sameAccount = (reportId, currentAccountId) =>
  String(reportId).toLowerCase() === String(currentAccountId).toLowerCase();

// The date to show, or null when received_at is missing or not a real date -- a bad value
// must not throw inside the upload screens' error callbacks.
const formattedDate = (data, receivedAt) => {
  if (!Number.isFinite(receivedAt)) return null;
  const date = new Date(receivedAt);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(`${data?.t?.lang || "en"}-CA`, { dateStyle: "long" }).format(date);
};

const otherReportKey = (hasNumber, hasDate) => {
  if (hasNumber) return hasDate ? "error_other_report" : "error_other_report_no_date";
  return hasDate ? "error_other_report_no_number" : "error_other_report_no_number_no_date";
};

// One pass over the template: a value is never searched for further placeholders, and a
// replacer function keeps a "$" in a report number as written. A missing translation
// (Translator.read gives undefined) yields "" rather than throwing inside an upload
// screen's error callback, which would leave it on "Uploading".
const fill = (template, values) => {
  if (typeof template !== "string") return "";
  return template.replace(/\{(\w+)\}/g, (placeholder, name) => (name in values ? values[name] : placeholder));
};

/**
 * The message for a duplicate upload refusal, or null when the refusal is something else.
 *
 * @param {object} data - The app store; data.t reads translations and holds the language.
 * @param {*} errorBody - The refusal's response body (response.response.data).
 * @param {string} currentAccountId - The expense account the receipt was uploaded to.
 * @returns {{text: string, reportId: (string|null)}|null} The translated text and, when
 *   the receipt is in another expense account the server named by a valid id, that
 *   account's id for a link; null when errorBody is not a duplicate refusal, so the caller
 *   keeps its text.
 */
export const duplicateUploadMessage = (data, errorBody, currentAccountId) => {
  if (!errorBody || typeof errorBody !== "object" || errorBody.message !== DUPLICATE) return null;

  const read = (key) => data.t.read(`${KEYS}.${key}`);
  const report = errorBody.expenses_account;
  if (!report || !report.id) return { text: read("error_unknown_report"), reportId: null };
  if (sameAccount(report.id, currentAccountId)) return { text: read("error_same_report"), reportId: null };

  const hasNumber = Boolean(report.identifiant);
  const date = formattedDate(data, errorBody.received_at);
  const text = fill(read(otherReportKey(hasNumber, date !== null)), {
    identifiant: hasNumber ? String(report.identifiant) : "",
    date: date ?? "",
  });
  const reportId = String(report.id);
  return { text, reportId: ACCOUNT_ID.test(reportId) ? reportId : null };
};

/**
 * The message for an approval action refused because the receipt is already in another
 * expense account, or null when the refusal is something else.
 *
 * @param {object} data - The app store; data.t reads translations.
 * @param {*} body - The refusal's response body (response.response.data), or one row of a
 *   bulk approval response.
 * @param {"approver"|"requester"} actor - "approver" when the user was approving the line
 *   (they may deny it instead); "requester" for the user's own receipt.
 * @returns {string|null} The translated text naming the other expense account when the
 *   server gave its number; null when body is not this refusal or the text is missing, so
 *   the caller keeps its own text.
 */
export const duplicateApprovalMessage = (data, body, actor) => {
  if (body?.error !== DUPLICATE_IN_OTHER_REPORT && body?.reason !== DUPLICATE_IN_OTHER_REPORT) return null;

  const identifiant = body.expenses_account?.identifiant;
  const hasNumber = Boolean(identifiant);
  const template = data.t.read(`${KEYS}.error_approval_${actor}${hasNumber ? "" : "_no_number"}`);
  if (typeof template !== "string") return null;
  return fill(template, { identifiant: hasNumber ? String(identifiant) : "" });
};
